import { useEffect, useRef, useState } from 'react';

export default function AppDownload() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="app" ref={sectionRef} className="py-20 bg-gradient-to-br from-[#184187] to-[#0f2d6b] relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Right Side - Text Content */}
          <div
            className={`text-right transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="inline-block bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full mb-6">
              <span className="text-white text-sm font-medium">قريباً جداً</span>
            </div>

            <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              تطبيق سماعتي
            </h2>

            <p className="text-xl text-white/90 leading-relaxed mb-8">
              نعمل على إطلاق تطبيق سماعتي والذي سيجعل من اقتناء المنتج المناسب واستدعاء الأخصائي أو الأخصائية السعوديين المتميزين والذي يزخر بهم فريق سماعتي مجرد ضغطة زر.
            </p>

            {/* Features List */}
            <div className="space-y-4 mb-10">
              {[
                'حجز موعد فوري مع أخصائي سمع',
                'تصفح المنتجات والسماعات الطبية',
                'متابعة حالتك الصحية',
                'استشارات عن بُعد',
              ].map((feature, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-3 transition-all duration-1000 delay-${(index + 1) * 100}`}
                  style={{
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateX(0)' : 'translateX(20px)',
                    transitionDelay: `${(index + 1) * 100}ms`,
                  }}
                >
                  <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span className="text-white/90 text-lg">{feature}</span>
                </div>
              ))}
            </div>

            {/* Download Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#"
                className="bg-white text-[#184187] px-8 py-4 rounded-xl font-medium hover:bg-gray-100 transition-all hover:scale-105 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer shadow-lg"
              >
                <i className="ri-apple-fill text-3xl"></i>
                <div className="text-right">
                  <div className="text-xs opacity-80">متاح على</div>
                  <div className="text-lg font-bold">App Store</div>
                </div>
              </a>

              <a
                href="#"
                className="bg-white text-[#184187] px-8 py-4 rounded-xl font-medium hover:bg-gray-100 transition-all hover:scale-105 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer shadow-lg"
              >
                <i className="ri-google-play-fill text-3xl"></i>
                <div className="text-right">
                  <div className="text-xs opacity-80">متاح على</div>
                  <div className="text-lg font-bold">Google Play</div>
                </div>
              </a>
            </div>
          </div>

          {/* Left Side - App Mockup */}
          <div
            className={`relative transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <div className="relative w-full max-w-md mx-auto">
              {/* Floating Elements */}
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-pulse"></div>
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white/10 rounded-full blur-2xl animate-pulse delay-1000"></div>

              {/* Phone Mockup */}
              <div className="relative z-10">
                <img
                  src="https://public.readdy.ai/ai/img_res/cbd5f1ddaaec3cc302d62b335d19b7eb.jpg"
                  alt="تطبيق سماعتي"
                  className="w-full h-auto drop-shadow-2xl"
                />
              </div>

              {/* Decorative Icons */}
              <div className="absolute top-20 -left-8 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center animate-bounce">
                <i className="ri-calendar-check-line text-3xl text-white"></i>
              </div>

              <div className="absolute bottom-32 -right-8 w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center animate-bounce delay-500">
                <i className="ri-notification-3-line text-3xl text-white"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
